import React, { FC, memo, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { institutionItemSelectors } from '@ucheba/store/institutions/item'
import { EInstitutionType } from '@ucheba/store/institutions/item/types'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'

import { analyticEvent } from '@ucheba/utils/helpers/analytics/events'
import {
  EAnalyticEvents,
  EAnalyticEventTargets,
  EAnalyticSections,
} from '@ucheba/utils/helpers/analytics/events/types'
import { EActivityInfoPart, EActivityTypes } from '@ucheba/utils/types/userActivityTypes'
import { authSelectors } from '@ucheba/store/auth'
import { Banner } from '../../../../../../components/Banner/desktop'
import LayoutInstitution from '../../../../../../components/LayoutInstitution/desktop'

import Rates from '../../../../../../components/InstitutionRates/desktop'
import Benefits from '../../../../../../components/InstitutionBenefits/desktop'
import Rankings from '../../../../../../components/InstitutionRankings/desktop'
import About from '../../../../../../components/InstitutionAbout/desktop'
import MainInfo from '../../../../../../components/InstitutionMainInfo/desktop'
import Campus from '../../../../../../components/InstitutionCampus/desktop'
import Licenses from '../../../../../../components/InstitutionLicenses/desktop'
import PartnerEmployers from './PartnerEmployers'
import GraduatesJob from '../../../../../../components/InstitutionGraduatesJob/desktop'
import Exams from '../../../../../../components/InstitutionExams/desktop'
import SeeAlso from '../../../../../../components/InstitutionSeeAlso/desktop'
import Olympiads from './Olympiads'
import Questions from './Questions'
import Feedback from './Feedback'
import Events from './Events'
import News from './News'
import AnnouncementOfNextEvent from './Events/AnnouncementOfNextEvent'
import InstitutionPrograms from '../../../../../../components/InstitutionPrograms/desktop'
import { RotationBannerAndAdmissionInstructions } from '../../../../components/RotationBannerAndAdmissionInstructions/desktop'
import { useBannerProgramTypes } from '../../utils/useBannerProgramTypes'
import { isPaidPackage } from '../../utils/isPaidPackage'
import LidMagnetForm from '../../../../../../components/LidMagnetForm/desktop'
import {
  EApplicationFormProductTypes,
  EApplicationFunnels,
} from '../../../../../../components/AppliactionForm/types'
import { ETypeRequest } from '../../../../../for-abiturients/ege/index/types'
import { useUserActivity } from '../../../../../../../utils/hooks/useUserActivity'

const InstitutionIdPage: FC = () => {
  const data = useSelector(institutionItemSelectors.currentInstitution)
  const isAuth = useSelector(authSelectors.isAuth)

  const {
    currentAverageSummary,
    averageSummary,
    maximumSummary,
    rankings,
    type,
    schoolInfo,
    abroadInfo,
    tags,
    advantages,
    campus,
    descriptionCampus,
    campusMediaList,
    egeForAdmission,
    partnerCompanies,
    companiesGraduateJobList,
    license,
    dateLicense,
    accreditation,
    dateAccreditation,
    seoLinks,
    description,
    hasPrograms,
    paidPackage,
    id,
    parent,
  } = data || {}

  const showRates =
    (type?.value === EInstitutionType.University ||
      type?.value === EInstitutionType.College) &&
    currentAverageSummary &&
    averageSummary &&
    maximumSummary

  const showRanking =
    rankings?.length > 0 &&
    (type?.value === EInstitutionType.University ||
      type?.value === EInstitutionType.College)

  const showAbout = [
    EInstitutionType.University,
    EInstitutionType.ForeignUniversity,
    EInstitutionType.College,
  ].includes(type?.value)

  const { foundYear, hasHostel, isMilitaryChair } = data
  const aboutBlockChips = { foundYear, hasHostel, isMilitaryChair }

  const isMainInfo = [
    EInstitutionType.School,
    EInstitutionType.Kindergarten,
    EInstitutionType.ForeignUniversity,
  ].includes(type?.value)

  const isSchoolOrTrainingCenterOrForeignUniversity = [
    EInstitutionType.School,
    EInstitutionType.TrainingCenter,
    EInstitutionType.ForeignUniversity,
  ].includes(type?.value)

  // показываем плашку "Есть\Нет военный учебный центр" если это не колледж и не зарубежный ВУЗ
  const isShowMilitaryTrainingCenter = ![
    EInstitutionType.College,
    EInstitutionType.ForeignUniversity,
  ].includes(type?.value)

  const { extraParams } = useBannerProgramTypes()

  const { setUserActivity } = useUserActivity()

  const handleActivity = async (): Promise<void> => {
    await setUserActivity({
      activityType: EActivityTypes.learning_request,
      institutionId: id,
      data: {
        infoPart: EActivityInfoPart.lead_form,
      },
    })
  }

  const showLidForm = useMemo(() => {
    // если это МГУ, это показываем только хим факу МГУ
    if (parent?.id === 1153 && id !== 51731) {
      return false
    }

    // если это Цифровой колледж Skillbox не показываем форму
    if (id === 109094) {
      return false
    }

    return (
      Number(id) !== 57067 &&
      Number(id) !== 5908 &&
      parent?.id !== 5908 &&
      Number(id) !== 6024
    )
  }, [id, parent])

  const LidMagnet = (
    <>
      {type?.value === EInstitutionType.College && showLidForm && (
        <>
          <LidMagnetForm
            title='Хочешь сдать ОГЭ на&nbsp;5+ и&nbsp;поступить в&nbsp;этот колледж на&nbsp;бюджет?'
            text='Поможем подготовиться к&nbsp;ОГЭ на&nbsp;высокие баллы с&nbsp;гарантией поступления в&nbsp;онлайн-школе Учеба.ру'
            buttonText='Подготовиться к ОГЭ'
            funnel={EApplicationFunnels.institution_main}
            productType={EApplicationFormProductTypes.oge}
            requestTarget={ETypeRequest.ege_lead_request}
            size='small'
            list={[
              'Расскажем о системе эффективной подготовки',
              'Познакомим с онлайн-школой и платформой',
              'Ответим на вопросы и запишем на занятия',
            ]}
            onSubmitForm={() => {
              analyticEvent({
                targets: [EAnalyticEventTargets.ym, EAnalyticEventTargets.ur],
                eventName: EAnalyticEvents.leadFormRequestCollege,
                section: EAnalyticSections.leadMagnetForm,
              })

              if (isPaidPackage(paidPackage) && isAuth) {
                handleActivity()
              }
            }}
            onCheckedCode={() => {
              if (isPaidPackage(paidPackage)) {
                handleActivity()
              }
            }}
          />

          <Spacing spacing='uxlarge' />
        </>
      )}

      {type?.value === EInstitutionType.University && showLidForm && (
        <>
          <LidMagnetForm
            title='Хочешь сдать ЕГЭ на&nbsp;80+ и&nbsp;поступить в&nbsp;этот вуз на&nbsp;бюджет?'
            text='Поможем подготовиться к&nbsp;ЕГЭ на&nbsp;высокие баллы с&nbsp;гарантией поступления в&nbsp;онлайн-школе Учеба.ру'
            buttonText='Подготовиться к ЕГЭ'
            funnel={EApplicationFunnels.institution_main}
            productType={EApplicationFormProductTypes.ege}
            requestTarget={ETypeRequest.ege_lead_request}
            size='small'
            list={[
              'Расскажем о системе эффективной подготовки',
              'Познакомим с онлайн-школой и платформой',
              'Ответим на вопросы и запишем на занятия',
            ]}
            onSubmitForm={() => {
              analyticEvent({
                targets: [EAnalyticEventTargets.ym, EAnalyticEventTargets.ur],
                eventName: EAnalyticEvents.leadFormRequestVuz,
                section: EAnalyticSections.leadMagnetForm,
              })

              if (isPaidPackage(paidPackage) && isAuth) {
                handleActivity()
              }
            }}
            onCheckedCode={() => {
              if (isPaidPackage(paidPackage)) {
                handleActivity()
              }
            }}
          />

          <Spacing spacing='uxlarge' />
        </>
      )}
    </>
  )

  return (
    <>
      <LayoutInstitution>
        {/* Баннер "Ближайшее мероприятие" */}
        {isPaidPackage(paidPackage) && <AnnouncementOfNextEvent />}

        {/* Ключевые показатели */}
        {showRates && (
          <>
            <Rates
              type={type.value}
              currentSummary={currentAverageSummary}
              averageSummary={averageSummary}
              maximumSummary={maximumSummary}
            />

            <Spacing spacing='uxlarge' />
          </>
        )}

        {data.admissionInstruction ? (
          <>
            {/* Инструкция по поступлению и горизонтальный баннер */}
            <RotationBannerAndAdmissionInstructions />

            <Spacing spacing='uxlarge' />
          </>
        ) : (
          <>{LidMagnet}</>
        )}

        {description && (
          <>
            <About
              isShowMilitaryTrainingCenter={isShowMilitaryTrainingCenter}
              description={description}
              institutionTypeName={type.name}
              chipsData={aboutBlockChips}
              showAbout={showAbout}
              institutionType={type.value}
            />

            <Spacing spacing='uxlarge' />
          </>
        )}

        {data.admissionInstruction ? (
          <>{LidMagnet}</>
        ) : (
          <>
            {/* Инструкция по поступлению и горизонтальный баннер */}
            <RotationBannerAndAdmissionInstructions />

            <Spacing spacing='uxlarge' />
          </>
        )}

        {/* Вуз в рейтингах */}
        {showRanking && (
          <>
            <Rankings type={type?.value} rankings={rankings} />

            <Spacing spacing='uxlarge' />
          </>
        )}

        {/* Преимущества */}
        {!!advantages?.length && isPaidPackage(paidPackage) && (
          <>
            <Benefits items={advantages} />

            <Spacing spacing='uxlarge' />
          </>
        )}

        {/* Программы обучения */}
        {hasPrograms && <InstitutionPrograms id={data.id} />}

        {isSchoolOrTrainingCenterOrForeignUniversity && !isPaidPackage(paidPackage) && (
          <Banner
            id={2}
            extraParams={extraParams}
            spacing={{
              bottom: 'uxlarge',
            }}
          />
        )}

        {!isPaidPackage(paidPackage) && !isSchoolOrTrainingCenterOrForeignUniversity && (
          <Banner
            id={2}
            extraParams={extraParams}
            spacing={{
              bottom: 'uxlarge',
              top: 'uxlarge',
            }}
          />
        )}

        {/* С какими ЕГЭ можно поступать */}
        {!!egeForAdmission?.length && (
          <>
            <Exams data={egeForAdmission} />

            <Spacing spacing='uxlarge' />
          </>
        )}

        {/* Камус и инфраструктура */}
        {((campus && !!campus.length) ||
          descriptionCampus ||
          (campusMediaList && !!campusMediaList.length)) && (
          <>
            <Campus
              description={descriptionCampus}
              campus={campus}
              mediaList={campusMediaList}
            />

            <Spacing spacing='uxlarge' />
          </>
        )}

        {/* Отзывы */}
        <Feedback />

        {/* Основная информация */}
        {isMainInfo && (
          <>
            <MainInfo
              type={type.value}
              schoolInfo={schoolInfo}
              abroadInfo={abroadInfo}
              tags={tags}
            />

            <Spacing spacing='uxlarge' />
          </>
        )}

        {isSchoolOrTrainingCenterOrForeignUniversity && !isPaidPackage(paidPackage) && (
          <Banner
            id={2}
            extraParams={extraParams}
            spacing={{
              bottom: 'uxlarge',
            }}
          />
        )}

        {!isPaidPackage(paidPackage) && !isSchoolOrTrainingCenterOrForeignUniversity && (
          <Banner
            id={2}
            extraParams={extraParams}
            spacing={{
              bottom: 'uxlarge',
              top: 'uxlarge',
            }}
          />
        )}

        {/* /!* Преподаватели *!/ */}
        {/* {!!teachers?.length && ( */}
        {/*  <> */}
        {/*    <Teachers teachers={teachers} /> */}

        {/*    <Spacing spacing='uxlarge' /> */}
        {/*  </> */}
        {/* )} */}

        {/* Где работают выпускники */}
        {!!companiesGraduateJobList.length && (
          <>
            <GraduatesJob list={companiesGraduateJobList} />

            <Spacing spacing='uxlarge' />
          </>
        )}

        {/* Работодатели и партнеры */}
        {!!partnerCompanies.length && (
          <>
            <PartnerEmployers list={partnerCompanies} />

            <Spacing spacing='uxlarge' />
          </>
        )}

        {/* Дни открытых дверей */}
        <Events />

        {/* Олимпиады вуза */}
        <Olympiads />

        {/* Новости */}
        {isPaidPackage(paidPackage) && <News />}

        {/* Вопросы */}
        {isPaidPackage(paidPackage) ? <Questions /> : null}

        {/* Лицензии и документы */}
        {(license || dateLicense || accreditation || dateAccreditation) && (
          <>
            <Licenses
              license={license}
              dateLicense={dateLicense}
              accreditation={accreditation}
              dateAccreditation={dateAccreditation}
            />

            <Spacing spacing='uxlarge' />
          </>
        )}
        {/* Смотрите также */}
        {!!seoLinks?.length && (
          <>
            <SeeAlso links={seoLinks} />

            <Spacing spacing='uxlarge' />
          </>
        )}
      </LayoutInstitution>

      {!isPaidPackage(paidPackage) && <Banner id={6} extraParams={extraParams} />}

      {!isPaidPackage(paidPackage) && <Banner id={14} extraParams={extraParams} />}
    </>
  )
}

export default memo(InstitutionIdPage)
